/**
 * ConversionScopeView - Manual Conversion Control with VS Code-style Editor
 *
 * MOCKUP - January 22, 2026
 *
 * This view provides user control over the conversion process:
 * - Select which paragraphs to convert
 * - Edit AI-suggested names before generation
 * - VS Code-style split view (COBOL left, Java right)
 * - AI assistance for individual methods
 *
 * Access: Landing page link or Workflow page link
 *
 * This is an ALTERNATIVE to the automated flow, not a replacement.
 */

import { useState, useEffect } from 'react'
import {
  Check, X, ChevronRight, ChevronDown, Sparkles, Code2,
  FileCode, Play, Save, RefreshCw, Eye, EyeOff,
  CheckSquare, Square, Edit3, Wand2, Download
} from 'lucide-react'
import './ConversionScopeView.css'

// Mock data for demonstration
const MOCK_PARAGRAPHS = [
  { id: '000', cobolName: '000-MAIN-CONTROL', aiName: 'mainControl', service: 'MainService', lines: '10-45', complexity: 'low', selected: true },
  { id: '100', cobolName: '100-INITIALIZE', aiName: 'initialize', service: 'MainService', lines: '46-89', complexity: 'low', selected: true },
  { id: '200', cobolName: '200-PROCESS-CUSTOMER', aiName: 'processCustomer', service: 'CustomerService', lines: '90-234', complexity: 'high', selected: true },
  { id: '300', cobolName: '300-VALIDATE-INPUT', aiName: 'validateInput', service: 'ValidationService', lines: '235-312', complexity: 'medium', selected: false },
  { id: '400', cobolName: '400-CALC-TAX', aiName: 'calculateTax', service: 'TaxService', lines: '313-445', complexity: 'high', selected: true },
  { id: '500', cobolName: '500-WRITE-OUTPUT', aiName: 'writeOutput', service: 'OutputService', lines: '446-512', complexity: 'medium', selected: true },
  { id: '600', cobolName: '600-ERROR-HANDLER', aiName: 'handleError', service: 'ErrorService', lines: '513-598', complexity: 'low', selected: false },
  { id: '700', cobolName: '700-CLEANUP', aiName: 'cleanup', service: 'MainService', lines: '599-634', complexity: 'low', selected: true },
]

const MOCK_COBOL_CODE = `       IDENTIFICATION DIVISION.
       PROGRAM-ID. CUSTPROC.
      *****************************************************************
      * CUSTOMER PROCESSING PROGRAM
      * Handles customer data validation and tax calculation
      *****************************************************************

       DATA DIVISION.
       WORKING-STORAGE SECTION.
       01  WS-CUSTOMER-RECORD.
           05  WS-CUST-ID          PIC X(10).
           05  WS-CUST-NAME        PIC X(30).
           05  WS-CUST-BALANCE     PIC 9(7)V99.
           05  WS-TAX-RATE         PIC 9V999.

       PROCEDURE DIVISION.

       000-MAIN-CONTROL.
           PERFORM 100-INITIALIZE
           PERFORM 200-PROCESS-CUSTOMER
           PERFORM 400-CALC-TAX
           PERFORM 500-WRITE-OUTPUT
           PERFORM 700-CLEANUP
           STOP RUN.

       200-PROCESS-CUSTOMER.
      *    Process customer data from input file
           READ CUSTOMER-FILE INTO WS-CUSTOMER-RECORD
               AT END SET END-OF-FILE TO TRUE
           END-READ
           IF NOT END-OF-FILE
               PERFORM 300-VALIDATE-INPUT
               IF VALID-INPUT
                   MOVE WS-CUST-ID TO OUTPUT-CUST-ID
                   MOVE WS-CUST-NAME TO OUTPUT-CUST-NAME
               END-IF
           END-IF.

       400-CALC-TAX.
      *    Calculate tax based on customer balance
           COMPUTE WS-TAX-AMOUNT =
               WS-CUST-BALANCE * WS-TAX-RATE
           IF WS-TAX-AMOUNT > 1000
               PERFORM 600-ERROR-HANDLER
           END-IF.`

const MOCK_JAVA_CODE = `/**
 * CustomerService - Generated from CUSTPROC.CBL
 *
 * @generated by ModernizeIT
 * @source CUSTPROC.CBL
 */
@Service
public class CustomerService {

    private static final Logger log = LoggerFactory.getLogger(CustomerService.class);

    @Autowired
    private CustomerRepository customerRepository;

    @Autowired
    private TaxService taxService;

    /**
     * Process customer data from input
     *
     * COBOL: 200-PROCESS-CUSTOMER (lines 90-234)
     */
    public CustomerResponse processCustomer(CustomerRequest request) {
        log.info("Processing customer: {}", request.getCustomerId());

        // Validate input
        ValidationResult validation = validateInput(request);
        if (!validation.isValid()) {
            throw new ValidationException(validation.getErrors());
        }

        // Process customer data
        Customer customer = customerRepository.findById(request.getCustomerId())
            .orElseThrow(() -> new CustomerNotFoundException(request.getCustomerId()));

        // Calculate tax
        BigDecimal taxAmount = taxService.calculateTax(customer.getBalance());

        return CustomerResponse.builder()
            .customerId(customer.getId())
            .customerName(customer.getName())
            .taxAmount(taxAmount)
            .build();
    }

    /**
     * Calculate tax based on customer balance
     *
     * COBOL: 400-CALC-TAX (lines 313-445)
     */
    public BigDecimal calculateTax(BigDecimal balance) {
        BigDecimal taxRate = new BigDecimal("0.075");
        BigDecimal taxAmount = balance.multiply(taxRate);

        if (taxAmount.compareTo(new BigDecimal("1000")) > 0) {
            handleError("Tax amount exceeds threshold");
        }

        return taxAmount;
    }
}`

export default function ConversionScopeView({ applicationName, jobId }) {
  const [paragraphs, setParagraphs] = useState(MOCK_PARAGRAPHS)
  const [selectedParagraph, setSelectedParagraph] = useState(MOCK_PARAGRAPHS[2]) // 200-PROCESS-CUSTOMER
  const [cobolCode, setCobolCode] = useState(MOCK_COBOL_CODE)
  const [javaCode, setJavaCode] = useState(MOCK_JAVA_CODE)
  const [editingName, setEditingName] = useState(null)
  const [showOnlySelected, setShowOnlySelected] = useState(false)
  const [aiGenerating, setAiGenerating] = useState(false)
  const [splitRatio, setSplitRatio] = useState(50) // 50% each side

  const selectedCount = paragraphs.filter(p => p.selected).length
  const totalCount = paragraphs.length

  const toggleParagraph = (id) => {
    setParagraphs(paragraphs.map(p =>
      p.id === id ? { ...p, selected: !p.selected } : p
    ))
  }

  const selectAll = () => {
    setParagraphs(paragraphs.map(p => ({ ...p, selected: true })))
  }

  const selectNone = () => {
    setParagraphs(paragraphs.map(p => ({ ...p, selected: false })))
  }

  const updateAiName = (id, newName) => {
    setParagraphs(paragraphs.map(p =>
      p.id === id ? { ...p, aiName: newName } : p
    ))
    setEditingName(null)
  }

  const handleAiAssist = async (paragraph) => {
    setAiGenerating(true)
    // Simulate AI generation
    await new Promise(resolve => setTimeout(resolve, 2000))
    setAiGenerating(false)
    // In real implementation, this would call the AI endpoint
  }

  const handleGenerateSelected = () => {
    const selected = paragraphs.filter(p => p.selected)
    console.log('Generating Java for:', selected.map(p => p.cobolName))
    // This would trigger Service Generation with only selected paragraphs
  }

  const getComplexityColor = (complexity) => {
    switch (complexity) {
      case 'high': return '#ef4444'
      case 'medium': return '#f59e0b'
      case 'low': return '#22c55e'
      default: return '#6b7280'
    }
  }

  const filteredParagraphs = showOnlySelected
    ? paragraphs.filter(p => p.selected)
    : paragraphs

  return (
    <div className="conversion-scope-view">
      {/* Header */}
      <div className="cs-header">
        <div className="cs-header-left">
          <Code2 size={24} />
          <div>
            <h1>Conversion Scope</h1>
            <span className="cs-subtitle">
              {applicationName || 'CUSTPROC'} • Manual Conversion Mode
            </span>
          </div>
        </div>
        <div className="cs-header-actions">
          <button className="cs-btn cs-btn-secondary">
            <Save size={16} />
            Save Scope
          </button>
          <button
            className="cs-btn cs-btn-primary"
            onClick={handleGenerateSelected}
            disabled={selectedCount === 0}
          >
            <Play size={16} />
            Generate Java ({selectedCount} selected)
          </button>
        </div>
      </div>

      <div className="cs-main">
        {/* Left Panel - Paragraph Selection */}
        <div className="cs-sidebar">
          <div className="cs-sidebar-header">
            <h3>Paragraphs</h3>
            <span className="cs-count">{selectedCount}/{totalCount}</span>
          </div>

          <div className="cs-sidebar-controls">
            <button onClick={selectAll} className="cs-link">Select All</button>
            <span className="cs-divider">|</span>
            <button onClick={selectNone} className="cs-link">Select None</button>
            <span className="cs-divider">|</span>
            <button
              onClick={() => setShowOnlySelected(!showOnlySelected)}
              className="cs-link"
            >
              {showOnlySelected ? <Eye size={14} /> : <EyeOff size={14} />}
              {showOnlySelected ? 'Show All' : 'Selected Only'}
            </button>
          </div>

          <div className="cs-paragraph-list">
            {filteredParagraphs.map(paragraph => (
              <div
                key={paragraph.id}
                className={`cs-paragraph-item ${selectedParagraph?.id === paragraph.id ? 'active' : ''} ${!paragraph.selected ? 'excluded' : ''}`}
                onClick={() => setSelectedParagraph(paragraph)}
              >
                <div className="cs-paragraph-checkbox" onClick={(e) => { e.stopPropagation(); toggleParagraph(paragraph.id) }}>
                  {paragraph.selected ? <CheckSquare size={18} className="checked" /> : <Square size={18} />}
                </div>

                <div className="cs-paragraph-info">
                  <div className="cs-paragraph-cobol">{paragraph.cobolName}</div>
                  <div className="cs-paragraph-meta">
                    {editingName === paragraph.id ? (
                      <input
                        type="text"
                        defaultValue={paragraph.aiName}
                        autoFocus
                        onBlur={(e) => updateAiName(paragraph.id, e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && updateAiName(paragraph.id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="cs-name-input"
                      />
                    ) : (
                      <span className="cs-paragraph-java" onClick={(e) => { e.stopPropagation(); setEditingName(paragraph.id) }}>
                        → {paragraph.aiName}()
                        <Edit3 size={12} className="cs-edit-icon" />
                      </span>
                    )}
                    <span className="cs-paragraph-service">{paragraph.service}</span>
                  </div>
                </div>

                <div
                  className="cs-complexity-dot"
                  style={{ backgroundColor: getComplexityColor(paragraph.complexity) }}
                  title={`${paragraph.complexity} complexity`}
                />
              </div>
            ))}
          </div>

          <div className="cs-sidebar-footer">
            <div className="cs-legend">
              <span><span className="dot" style={{backgroundColor: '#22c55e'}}></span> Low</span>
              <span><span className="dot" style={{backgroundColor: '#f59e0b'}}></span> Medium</span>
              <span><span className="dot" style={{backgroundColor: '#ef4444'}}></span> High</span>
            </div>
          </div>
        </div>

        {/* Right Panel - Code Editor Split View */}
        <div className="cs-editor-panel">
          {/* Editor Header */}
          <div className="cs-editor-header">
            <div className="cs-editor-tabs">
              <div className="cs-editor-tab active">
                <FileCode size={14} />
                COBOL Source
              </div>
              <div className="cs-editor-tab active">
                <Code2 size={14} />
                Java Output
              </div>
            </div>
            <div className="cs-editor-actions">
              <button
                className="cs-btn cs-btn-ai"
                onClick={() => handleAiAssist(selectedParagraph)}
                disabled={aiGenerating}
              >
                {aiGenerating ? (
                  <RefreshCw size={16} className="spinning" />
                ) : (
                  <Wand2 size={16} />
                )}
                {aiGenerating ? 'AI Generating...' : 'AI Assist'}
              </button>
              <button className="cs-btn cs-btn-icon" title="Download Java">
                <Download size={16} />
              </button>
            </div>
          </div>

          {/* Split Editor */}
          <div className="cs-editor-split">
            {/* COBOL Side */}
            <div className="cs-editor-pane" style={{ width: `${splitRatio}%` }}>
              <div className="cs-editor-pane-header">
                <span>CUSTPROC.CBL</span>
                <span className="cs-editor-line-info">
                  {selectedParagraph && `Lines ${selectedParagraph.lines}`}
                </span>
              </div>
              <div className="cs-code-container">
                <pre className="cs-code cs-code-cobol">
                  <code>{cobolCode}</code>
                </pre>
                {selectedParagraph && (
                  <div className="cs-highlight-overlay" data-paragraph={selectedParagraph.cobolName}>
                    {/* Highlight would be positioned over the selected paragraph */}
                  </div>
                )}
              </div>
            </div>

            {/* Resize Handle */}
            <div className="cs-editor-resize" />

            {/* Java Side */}
            <div className="cs-editor-pane" style={{ width: `${100 - splitRatio}%` }}>
              <div className="cs-editor-pane-header">
                <span>CustomerService.java</span>
                <span className="cs-editor-status">
                  <Sparkles size={12} />
                  AI Generated
                </span>
              </div>
              <div className="cs-code-container">
                <pre className="cs-code cs-code-java">
                  <code>{javaCode}</code>
                </pre>
              </div>
            </div>
          </div>

          {/* Editor Footer */}
          <div className="cs-editor-footer">
            <div className="cs-editor-info">
              {selectedParagraph && (
                <>
                  <span className="cs-info-item">
                    <strong>Selected:</strong> {selectedParagraph.cobolName}
                  </span>
                  <span className="cs-info-item">
                    <strong>Maps to:</strong> {selectedParagraph.aiName}()
                  </span>
                  <span className="cs-info-item">
                    <strong>Service:</strong> {selectedParagraph.service}
                  </span>
                </>
              )}
            </div>
            <div className="cs-editor-hints">
              <span>Click paragraph name to edit • Use AI Assist for method generation</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
